if (!customElements.get('product-inventory')) {
  class ProductInventory extends HTMLElement {
    constructor() {
      super();
      if (this.hidden) {
        this.initLazySection();
      } else {
        window.initLazyScript(this, this.initLazySection.bind(this));
      }
    }

    initLazySection() {
      this.inventoryNotice = this.querySelector('.js-inventory-notice');
      this.urgencyMessage = this.querySelector('.js-inventory-urgency');
      this.indicatorBar = this.querySelector('.js-inventory-indicator');
      this.variantInventory = this.getVariantInventory();

      // Bind to on:variant:change event for this product
      const variantPicker = this.closest('.product-info').querySelector('variant-picker');
      if (variantPicker) {
        variantPicker.addEventListener('on:variant:change', this.handleVariantChange.bind(this));
      }

      // An empty availability value means no variant has been selected yet.
      if (this.dataset.variantAvailable === 'true' || this.dataset.variantAvailable === 'false') {
        this.updateInventory(
          parseInt(this.dataset.inventoryQuantity, 10),
          this.dataset.variantAvailable === 'true',
          this.dataset.inventoryPolicy,
          this.dataset.supplierAvailability
        );
      }
    }

    /**
     * Gets the inventory data for all product variants
     * @returns {?object}
     */
    getVariantInventory() {
      const dataEl = this.querySelector('[type="application/json"]');
      return this.variantInventory || JSON.parse(dataEl.textContent);
    }

    /**
     * Handles a 'change' event on the variant picker element
     * @param {object} evt - Event object
     */
    handleVariantChange(evt) {
      if (evt.detail.variant) {
        const inventory = this.variantInventory.filter(
          (variant) => variant.id === evt.detail.variant.id
        )[0];
        this.updateInventory(
          inventory.inventory_quantity,
          inventory.available,
          inventory.inventory_policy,
          inventory.supplier_availability
        );
      } else {
        this.hidden = true;
      }
    }

    /**
     *
     * Updates the inventory notice
     * @param {number} count - the inventory quantity available
     * @param {boolean} available - whether current variant is available
     * @param {string} inventoryPolicy - whether product continues selling when out of stock
     * @param {string} supplierAvailability - native status for the selected variant, if present
     */
    updateInventory(count, available, inventoryPolicy, supplierAvailability) {
      let inventoryLevel;
      const thresholdLow = parseInt(this.dataset.thresholdLow, 10);
      const thresholdVeryLow = parseInt(this.dataset.thresholdVeryLow, 10);
      const sellWhenOutOfStock = inventoryPolicy === 'continue';
      const showLowCount = available && count > 0 && count <= thresholdLow;
      const showZeroAsAvailable = available && count <= 0 && sellWhenOutOfStock;
      const hasSupplierStatus = available && ['in_stock', 'on_order', 'review_required'].includes(supplierAvailability);

      if (!available) {
        inventoryLevel = 'none';
      } else if (hasSupplierStatus) {
        inventoryLevel = supplierAvailability;
      } else if (showZeroAsAvailable) {
        inventoryLevel = 'in_stock';
      } else if (count <= 0) {
        inventoryLevel = 'in_stock';
      } else if (count <= thresholdVeryLow) {
        inventoryLevel = 'very_low';
      } else if (count <= thresholdLow) {
        inventoryLevel = 'low';
      } else {
        inventoryLevel = 'normal';
      }

      // Always show availability for a selected variant; quantity/urgency remain optional.
      this.hidden = false;

      // Set the inventory level data attribute
      this.setAttribute('data-inventory-level', inventoryLevel);

      // Determine whether to show the count or not
      if (hasSupplierStatus) {
        this.inventoryNotice.innerText = inventoryLevel === 'on_order'
          ? this.dataset.textSupplierOnOrder
          : inventoryLevel === 'review_required'
            ? this.dataset.textSupplierReview
            : theme.strings.inStock;
      } else if (showLowCount && (this.dataset.showCount === 'always' || this.dataset.showCount === 'low')) {
        this.inventoryNotice.innerText = theme.strings.onlyXLeft.replace('[quantity]', count);
      } else if (inventoryLevel === 'very_low') {
        this.inventoryNotice.innerText = theme.strings.veryLowStock;
      } else if (inventoryLevel === 'low') {
        this.inventoryNotice.innerText = theme.strings.lowStock;
      } else if (inventoryLevel === 'normal' || inventoryLevel === 'in_stock') {
        this.inventoryNotice.innerText = theme.strings.inStock;
      } else if (inventoryLevel === 'none') {
        this.inventoryNotice.innerText = theme.strings.noStock;
      }

      // Update urgency message if needed
      if (this.urgencyMessage) {
        if (hasSupplierStatus) {
          this.urgencyMessage.innerHTML = '';
        } else if (inventoryLevel === 'very_low') {
          this.urgencyMessage.innerHTML = this.dataset.textVeryLow;
        } else if (inventoryLevel === 'low') {
          this.urgencyMessage.innerHTML = this.dataset.textLow;
        } else if (inventoryLevel === 'normal' || inventoryLevel === 'in_stock') {
          this.urgencyMessage.innerHTML = showZeroAsAvailable ? '' : this.dataset.textNormal;
        } else if (inventoryLevel === 'none') {
          this.urgencyMessage.innerHTML = this.dataset.textNoStock;
        }
      }

      // Update the bar indicator. Hide it for zero-as-available to avoid exposing 0 stock.
      if (this.indicatorBar) {
        this.indicatorBar.hidden = hasSupplierStatus || showZeroAsAvailable || !showLowCount;

        let newWidth;
        if (count >= this.dataset.scale || inventoryLevel === 'in_stock') {
          newWidth = 100;
        } else {
          newWidth = ((100 / parseInt(this.dataset.scale, 10)) * count).toFixed(1);
        }
        this.indicatorBar.querySelector('span').style.width = `${newWidth}%`;
      }
    }

  }

  customElements.define('product-inventory', ProductInventory);
}
